import { pushState } from "$app/navigation"
import type { Optional } from "$lib/types"
import hash from "object-hash"
import { mount, unmount, type Component } from "svelte"
import InformationalDialog from "./InformationalDialog.svelte"
import SecurityInformationDialog from "./SecurityInformationDialog.svelte"
import VisaInfoDialog from "./VisaInfoDialog.svelte"

type OnCloseResult<T> = T extends (...args: infer Args) => void
  ? Args extends [infer R]
    ? R
    : void
  : void

/**
 * open a dialog and return a promise that resolves when the dialog is closed/resolved
 *
 * @param DialogComponent the component to render
 * @param props the props to pass to the component
 * @param target the target element to render the component in
 * @returns a promise that resolves when the component is closed
 */
export function asyncDialog<
  P extends Record<string, any>,
  // `Result` defaults to the inferred onClose-arg of P, preserving the
  // existing call ergonomics (`asyncDialog(MyDialog, props)` resolves to
  // whatever MyDialog's onClose receives). Splitting it into a free generic
  // also lets call-site assignments to broader function types (e.g.
  // `OpenDialogFn<T>` from caim-shared-ui) bind `Result` directly when TS
  // forces `P` to widen to `any`.
  Result = OnCloseResult<P["onClose"]>,
>(
  DialogComponent: Component<P>,
  props = {} as Optional<P, "onClose">,
  target = document.body
): Promise<Result | null> {
  let dialog: object

  return new Promise<Result | null>((resolve, _reject) => {
    // For Svelte 5 components that use callback props
    const onClose = (result: Result) => {
      ;(props.onClose as ((result: Result) => void) | undefined)?.(result)
      resolve(result ?? null)
      unmount(dialog)
    }

    // Merge onClose callback into props for Svelte 5 components.
    // `unknown` two-step: TS can't statically prove that the spread + injected
    // onClose recreates the original P (P could be a stricter subtype of the
    // constraint), but at runtime the merged object satisfies the component's
    // declared props.
    const dialogProps = { ...props, onClose } as unknown as P

    dialog = mount(DialogComponent, {
      props: dialogProps,
      target,
    })
  })
}

/**
 * serialize the dialog component and props and push it onto the browser history
 * @param DialogComponent the component to render
 * @param props the props to pass to the component
 */
export function pushDialog<P extends Record<string, any>>(
  DialogComponent: Component<P>,
  props?: P
) {
  pushState("", {
    dialog: { componentHash: hash(DialogComponent), props: props ?? {} },
  })
}

const DIALOG_COMPONENTS = new Map<string, Component<any>>([
  [hash(InformationalDialog), InformationalDialog],
  [hash(SecurityInformationDialog), SecurityInformationDialog],
  [hash(VisaInfoDialog), VisaInfoDialog],
] as const)

/**
 * get the dialog component and props from the page state
 * @package for use within $lib/dialog
 * @param pageState the svelte kit page state
 * @returns the dialog component and props
 */
export function getStatefulDialog({ dialog }: App.PageState) {
  if (!dialog) return { Component: null, props: {} }
  const { props, componentHash } = dialog
  const originalOnClose = props.onClose as any
  const onClose = () => {
    originalOnClose?.()
    history.back()
  }
  return {
    Component: DIALOG_COMPONENTS.get(componentHash),
    props: { ...props, onClose },
  }
}
